import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAcademicYearPicker } from '@/hooks/usePickers';
import {
  feeCategoriesApi,
  financeSettingsApi,
  lateFeesApi,
} from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { FeeCategory, LateFeeRule } from '@/types/finance';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The three things a school configures once and rarely revisits: what kinds of
 * fee exist, what happens when a bill goes unpaid, and how money is denominated
 * and numbered.
 *
 * <p>One screen rather than three, because they are set up together on the day
 * finance is switched on and then left alone. Three sparse entries in the
 * navigation would make the menu longer without making anything easier to find.
 */
export function FinanceSetupPage() {
  const { can } = useAuth();

  return (
    <div>
      <PageHeader
        title="Fee Setup"
        description="What the school charges for, what happens when a bill goes unpaid, and how money is denominated."
        icon={<Settings2 className="h-5 w-5" aria-hidden="true" />}
      />
      <div className="space-y-4">
        <CategoriesSection canManage={can(PERMISSIONS.FEE_CATEGORY_MANAGE)} />
        <LateFeesSection canManage={can(PERMISSIONS.LATE_FEE_MANAGE)}
                         canApply={can(PERMISSIONS.LATE_FEE_APPLY)} />
        {can(PERMISSIONS.FINANCE_SETTINGS_VIEW) && (
          <SettingsSection canManage={can(PERMISSIONS.FINANCE_SETTINGS_MANAGE)} />
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ categories

function CategoriesSection({ canManage }: { canManage: boolean }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<FeeCategory | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', description: '', refundable: false, lateFeeApplicable: true,
  });

  const query = useQuery({
    queryKey: queryKeys.feeCategories.list(false),
    queryFn: () => feeCategoriesApi.list(false),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code,
        name: form.name,
        ...(form.description !== '' ? { description: form.description } : {}),
        refundable: form.refundable,
        lateFeeApplicable: form.lateFeeApplicable,
      };
      return editing === null
        ? feeCategoriesApi.create(payload)
        : feeCategoriesApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Category added' : 'Category updated');
      setOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeCategories.all });
    },
    onError: (error) => handleApiError(error),
  });

  const retire = useMutation({
    mutationFn: (category: FeeCategory) =>
      feeCategoriesApi.changeStatus(category.id, {
        status: category.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
      }),
    onSuccess: () => {
      toast.success('Category updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeCategories.all });
    },
    onError: (error) => handleApiError(error),
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Fee categories"
          description="The kinds of fee the school charges. Never deleted — old invoice lines name their category and must stay readable."
          action={canManage ? (
            <Button size="sm" onClick={() => {
              setEditing(null);
              setForm({ code: '', name: '', description: '', refundable: false, lateFeeApplicable: true });
              setOpen(true);
            }}>Add a category</Button>
          ) : undefined}
        />
        {query.isPending ? (
          <LoadingState label="Loading categories" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {['Code', 'Name', 'Refundable', 'Late fees', 'Status', ''].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {(query.data ?? []).map((category) => (
                  <tr key={category.id}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-xs text-ink">{category.code}</span>
                      {category.systemDefined && (
                        <span className="ml-2 text-[10.5px] text-subtle">standard</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-ink">{category.name}</span>
                      {category.description !== undefined && (
                        <span className="block text-[11px] text-muted">{category.description}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted">{category.refundable ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2.5 text-muted">
                      {category.lateFeeApplicable ? 'May attract them' : 'Exempt'}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={category.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
                        {category.status.toLowerCase()}
                      </Badge>
                      {category.inUse && (
                        <span className="ml-1.5 text-[10.5px] text-subtle">in use</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {canManage && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditing(category);
                            setForm({
                              code: category.code, name: category.name,
                              description: category.description ?? '',
                              refundable: category.refundable,
                              lateFeeApplicable: category.lateFeeApplicable,
                            });
                            setOpen(true);
                          }}>Edit</Button>
                          <Button size="sm" variant="ghost" loading={retire.isPending}
                                  onClick={() => retire.mutate(category)}>
                            {category.status === 'ACTIVE' ? 'Retire' : 'Restore'}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={open}
        title={editing === null ? 'Add a fee category' : `Edit ${editing.name}`}
        onClose={() => { setOpen(false); setEditing(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button loading={save.isPending} disabled={form.code === '' || form.name === ''}
                    onClick={() => save.mutate()}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Code" required placeholder="SWIMMING"
                       disabled={editing?.systemDefined === true}
                       hint={editing?.systemDefined === true
                         ? 'A standard category keeps its code — reports and seeds refer to it.'
                         : 'Short, uppercase, and permanent.'}
                       value={form.code}
                       onChange={(event) => setForm((f) => ({ ...f, code: event.target.value.toUpperCase() }))} />
            <TextField label="Name" required placeholder="Swimming Fee" value={form.name}
                       onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} />
          </div>
          <TextField label="Description" value={form.description}
                     onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))} />
          <label className="flex items-start gap-2.5 border border-line bg-surface px-3.5 py-2.5">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.refundable}
                   onChange={(event) => setForm((f) => ({ ...f, refundable: event.target.checked }))} />
            <span>
              <span className="block text-[13px] font-medium text-ink">Ordinarily refundable</span>
              <span className="block text-xs text-muted">
                A security deposit is; a sat examination's fee is not. Advisory — an approver may
                still decide otherwise.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 border border-line bg-surface px-3.5 py-2.5">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.lateFeeApplicable}
                   onChange={(event) => setForm((f) => ({ ...f, lateFeeApplicable: event.target.checked }))} />
            <span>
              <span className="block text-[13px] font-medium text-ink">May attract a late fee</span>
              <span className="block text-xs text-muted">
                Late tuition and a late voluntary activity fee are different decisions.
              </span>
            </span>
          </label>
        </div>
      </Modal>
    </>
  );
}

// ------------------------------------------------------------------ late fees

function LateFeesSection({ canManage, canApply }: { canManage: boolean; canApply: boolean }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const years = useAcademicYearPicker();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LateFeeRule | null>(null);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweep, setSweep] = useState({ asOfDate: today() });
  const [preview, setPreview] = useState<{ examined: number; charged: number; already: number; noRule: number; total: number } | null>(null);
  const [form, setForm] = useState({
    name: '', feeCategoryId: '', academicYearId: '', chargeType: 'FIXED',
    value: '', frequency: 'ONCE', graceDays: '5', maxAmount: '', effectiveFrom: today(),
  });

  const rules = useQuery({ queryKey: queryKeys.lateFees.all, queryFn: () => lateFeesApi.list() });
  const categories = useQuery({
    queryKey: queryKeys.feeCategories.list(true),
    queryFn: () => feeCategoriesApi.list(true),
    enabled: open,
  });

  const recurring = form.frequency !== 'ONCE';

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        ...(form.feeCategoryId !== '' ? { feeCategoryId: form.feeCategoryId } : {}),
        ...(form.academicYearId !== '' ? { academicYearId: form.academicYearId } : {}),
        chargeType: form.chargeType,
        value: Number(form.value),
        frequency: form.frequency,
        graceDays: Number(form.graceDays),
        ...(form.maxAmount !== '' ? { maxAmount: Number(form.maxAmount) } : {}),
        effectiveFrom: form.effectiveFrom,
      };
      return editing === null
        ? lateFeesApi.create(payload)
        : lateFeesApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Rule created' : 'Rule updated');
      setOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.lateFees.all });
    },
    onError: (error) => handleApiError(error),
  });

  const runSweep = useMutation({
    mutationFn: (dryRun: boolean) => lateFeesApi.apply({ asOfDate: sweep.asOfDate, dryRun }),
    onSuccess: (result) => {
      if (result.dryRun) {
        setPreview({
          examined: result.invoicesExamined, charged: result.invoicesCharged,
          already: result.alreadyCharged, noRule: result.noRule, total: result.totalCharged,
        });
        return;
      }
      toast.success(`Charged ${result.totalCharged} across ${result.invoicesCharged} invoices`);
      setPreview(null);
      setSweepOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
    onError: (error) => handleApiError(error),
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Late fee rules"
          description="What is charged when a bill goes unpaid. Each rule is stated in words as well as numbers."
          action={
            <div className="flex gap-2">
              {canApply && (
                <Button size="sm" variant="secondary"
                        onClick={() => { setPreview(null); setSweepOpen(true); }}>
                  Charge late fees
                </Button>
              )}
              {canManage && (
                <Button size="sm" onClick={() => {
                  setEditing(null);
                  setForm({
                    name: '', feeCategoryId: '', academicYearId: '', chargeType: 'FIXED',
                    value: '', frequency: 'ONCE', graceDays: '5', maxAmount: '', effectiveFrom: today(),
                  });
                  setOpen(true);
                }}>Add a rule</Button>
              )}
            </div>
          }
        />
        {rules.isPending ? (
          <LoadingState label="Loading rules" />
        ) : (rules.data ?? []).length === 0 ? (
          <EmptyState title="No late fee rules"
                      description="Without a rule the overdue sweep charges nothing." />
        ) : (
          <ul className="divide-y divide-line">
            {(rules.data ?? []).map((rule) => (
              <li key={rule.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3">
                <span className="min-w-[150px] flex-1">
                  <span className="block text-[13px] font-medium text-ink">{rule.name}</span>
                  <span className="block text-xs text-muted">{rule.summary}</span>
                </span>
                <span className="text-[11px] text-subtle">
                  {rule.feeCategoryName ?? 'every category'}
                  {rule.academicYearName !== undefined && ` · ${rule.academicYearName}`}
                </span>
                <Badge tone={rule.status === 'ACTIVE' ? 'success' : 'neutral'}>
                  <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
                  {rule.status.toLowerCase()}
                </Badge>
                {canManage && (
                  <Button size="sm" variant="ghost" onClick={() => {
                    setEditing(rule);
                    setForm({
                      name: rule.name, feeCategoryId: rule.feeCategoryId ?? '',
                      academicYearId: rule.academicYearId ?? '', chargeType: rule.chargeType,
                      value: String(rule.value), frequency: rule.frequency,
                      graceDays: String(rule.graceDays),
                      maxAmount: rule.maxAmount === undefined ? '' : String(rule.maxAmount),
                      effectiveFrom: rule.effectiveFrom,
                    });
                    setOpen(true);
                  }}>Edit</Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'Add a late fee rule' : `Edit ${editing.name}`}
        onClose={() => { setOpen(false); setEditing(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button loading={save.isPending}
                    disabled={form.name === '' || form.value === '' || (recurring && form.maxAmount === '')}
                    onClick={() => save.mutate()}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="Name" required placeholder="Standard late fee" value={form.name}
                     onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} />

          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField label="Charge" required value={form.chargeType}
                         onChange={(event) => setForm((f) => ({ ...f, chargeType: event.target.value }))}>
              <option value="FIXED">A fixed amount</option>
              <option value="PERCENTAGE">A percentage of what is owed</option>
            </SelectField>
            <TextField label={form.chargeType === 'FIXED' ? 'Amount' : 'Percentage'}
                       type="number" step="0.01" required value={form.value}
                       onChange={(event) => setForm((f) => ({ ...f, value: event.target.value }))} />
            <SelectField label="How often" required value={form.frequency}
                         onChange={(event) => setForm((f) => ({ ...f, frequency: event.target.value }))}>
              <option value="ONCE">Once</option>
              <option value="DAILY">Every day</option>
              <option value="WEEKLY">Every week</option>
            </SelectField>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label="Grace period" type="number" min="0" required value={form.graceDays}
                       hint="Days after the due date before anything is charged."
                       onChange={(event) => setForm((f) => ({ ...f, graceDays: event.target.value }))} />
            <TextField label="Ceiling" type="number" step="0.01" required={recurring}
                       value={form.maxAmount}
                       hint={recurring
                         ? 'Required for a recurring charge — without one it grows without limit.'
                         : 'Optional.'}
                       onChange={(event) => setForm((f) => ({ ...f, maxAmount: event.target.value }))} />
            <TextField label="In force from" type="date" required value={form.effectiveFrom}
                       onChange={(event) => setForm((f) => ({ ...f, effectiveFrom: event.target.value }))} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label="Applies to" value={form.feeCategoryId}
                         hint="Blank covers every category that may attract late fees."
                         onChange={(event) => setForm((f) => ({ ...f, feeCategoryId: event.target.value }))}>
              <option value="">Every category</option>
              {(categories.data ?? []).filter((c) => c.lateFeeApplicable).map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </SelectField>
            <SelectField label="Academic year" value={form.academicYearId}
                         hint="Blank applies to every year."
                         onChange={(event) => setForm((f) => ({ ...f, academicYearId: event.target.value }))}>
              <option value="">Every year</option>
              {(years.data ?? []).map((year) => (
                <option key={year.id} value={year.id}>{year.name}</option>
              ))}
            </SelectField>
          </div>
        </div>
      </Modal>

      <Modal
        open={sweepOpen}
        title="Charge late fees"
        description="Works out the whole charge owed as at the date given and adds only what is not already charged."
        onClose={() => { setSweepOpen(false); setPreview(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setSweepOpen(false); setPreview(null); }}>
              Cancel
            </Button>
            <Button variant="secondary" loading={runSweep.isPending && runSweep.variables === true}
                    onClick={() => runSweep.mutate(true)}>Preview</Button>
            <Button disabled={preview === null}
                    loading={runSweep.isPending && runSweep.variables === false}
                    onClick={() => runSweep.mutate(false)}>
              {preview === null ? 'Preview first' : `Charge ${preview.charged} invoices`}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="As at" type="date" required value={sweep.asOfDate}
                     hint="Running it for a past date gives that date's answer, not today's."
                     onChange={(event) => setSweep({ asOfDate: event.target.value })} />
          <p className="text-xs text-muted">
            Safe to run twice: the second run finds the charge already standing and adds nothing.
          </p>
          {preview !== null && (
            <div className="border-2 border-line bg-surface px-4 py-3">
              <strong className="text-[13px] font-extrabold text-ink">
                Preview — nothing has been charged
              </strong>
              <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-4">
                <div><dt className="text-subtle">Overdue</dt><dd className="tabular-nums text-ink">{preview.examined}</dd></div>
                <div><dt className="text-subtle">To charge</dt><dd className="tabular-nums text-ink">{preview.charged}</dd></div>
                <div><dt className="text-subtle">Already charged</dt><dd className="tabular-nums text-ink">{preview.already}</dd></div>
                <div><dt className="text-subtle">Total</dt><dd className="tabular-nums text-ink">{preview.total}</dd></div>
              </dl>
              {preview.noRule > 0 && (
                <p className="mt-2 text-xs text-warning">
                  {preview.noRule} overdue invoice{preview.noRule === 1 ? ' has' : 's have'} no rule
                  covering them and will not be charged.
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

// ------------------------------------------------------------------- settings

function SettingsSection({ canManage }: { canManage: boolean }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    currencyCode: '', invoiceNumberPrefix: '', receiptNumberPrefix: '',
    refundNumberPrefix: '', defaultGraceDays: 0, allowAdvancePayment: true,
  });

  const query = useQuery({
    queryKey: queryKeys.finance.settings,
    queryFn: () => financeSettingsApi.get(),
  });

  const save = useMutation({
    mutationFn: () => financeSettingsApi.update(form),
    onSuccess: () => {
      toast.success('Finance settings updated');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (query.isPending) return <LoadingState label="Loading settings" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const settings = query.data;

  return (
    <>
      <Card>
        <CardHeader
          title="Currency and numbering"
          description="Set once. Changing a prefix affects future documents; numbers already issued keep theirs."
          action={canManage ? (
            <Button size="sm" variant="secondary" onClick={() => {
              setForm({
                currencyCode: settings.currencyCode,
                invoiceNumberPrefix: settings.invoiceNumberPrefix,
                receiptNumberPrefix: settings.receiptNumberPrefix,
                refundNumberPrefix: settings.refundNumberPrefix,
                defaultGraceDays: settings.defaultGraceDays,
                allowAdvancePayment: settings.allowAdvancePayment,
              });
              setEditing(true);
            }}>Edit</Button>
          ) : undefined}
        />
        <dl className="grid gap-x-6 gap-y-4 px-4 py-4 sm:grid-cols-3">
          <Fact label="Currency" value={settings.currencyCode} />
          <Fact label="Invoice numbers" value={`${settings.invoiceNumberPrefix}-…`} />
          <Fact label="Receipt numbers" value={`${settings.receiptNumberPrefix}-…`} />
          <Fact label="Refund numbers" value={`${settings.refundNumberPrefix}-…`} />
          <Fact label="Default grace" value={`${settings.defaultGraceDays} days`} />
          <Fact label="Money on account"
                value={settings.allowAdvancePayment ? 'May be held' : 'Not held'} />
        </dl>
      </Card>

      <Modal
        open={editing}
        title="Finance settings"
        onClose={() => setEditing(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="Currency code" required maxLength={3} value={form.currencyCode}
                     hint="Three letters, ISO 4217 — PKR, NGN, AED."
                     onChange={(event) =>
                       setForm((f) => ({ ...f, currencyCode: event.target.value.toUpperCase() }))} />
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label="Invoice prefix" required value={form.invoiceNumberPrefix}
                       onChange={(event) => setForm((f) => ({ ...f, invoiceNumberPrefix: event.target.value }))} />
            <TextField label="Receipt prefix" required value={form.receiptNumberPrefix}
                       onChange={(event) => setForm((f) => ({ ...f, receiptNumberPrefix: event.target.value }))} />
            <TextField label="Refund prefix" required value={form.refundNumberPrefix}
                       onChange={(event) => setForm((f) => ({ ...f, refundNumberPrefix: event.target.value }))} />
          </div>
          <TextField label="Default grace period" type="number" min="0" max="90"
                     value={String(form.defaultGraceDays)}
                     hint="Days after the due date before an invoice counts as overdue at all."
                     onChange={(event) =>
                       setForm((f) => ({ ...f, defaultGraceDays: Number(event.target.value) }))} />
          <label className="flex items-start gap-2.5 border border-line bg-surface px-3.5 py-2.5">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.allowAdvancePayment}
                   onChange={(event) =>
                     setForm((f) => ({ ...f, allowAdvancePayment: event.target.checked }))} />
            <span>
              <span className="block text-[13px] font-medium text-ink">
                Money beyond what is owed may be held on account
              </span>
              <span className="block text-xs text-muted">
                Schools differ on this, so it is a setting rather than a rule.
              </span>
            </span>
          </label>
        </div>
      </Modal>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
